import React, { useEffect } from 'react';
import clsx from 'clsx';
import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import { CSVLink } from 'react-csv';
import {
  dateFormat,
  flattenAssetData,
  getLocalDateTime,
} from '../../utils/utils';
import { GetString } from '../StringResourceManager';
import { LocaleInterface, locales } from '../AlertsGrid/GridLocale';
// Interfaces
import { getNodeAssetsInfo } from '../../store/Assets/AssetsActions';
import {
  NodeInfo,
  AssetInfo,
  GridRowAssetTableInfo,
} from '../../Interfaces/Assets/AssetInfo';
import AssetDetailComponent from './GridAssetRowDetailComponent';

const useStyles = makeStyles(theme => ({
  gridAlign: {
    display: 'flex',
    flex: 1,
  },
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    width: '25ch',
  },
  box: {
    height: 60,
    display: 'flex',
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
  spreadBox: {
    alignItems: 'flex-start',
  },
  dialogAction: {
    height: '3rem',
    padding: theme.spacing(3),
    marginRight: theme.spacing(1),
  },
  dialogLabel: {
    fontSize: '1.1rem',
    display: 'inline',
  },
  dialogTextBold: {
    fontWeight: 'bold',
  },
  container: {
    backgroundColor: 'white',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
}));
interface Props {
  assetData: NodeInfo;
  closePage: () => void;
}

const NodeAssetsForm: React.FC<Props> = ({ assetData, closePage }) => {
  const classes = useStyles();

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const dispatch: Dispatch<any> = useDispatch();

  const [assetsInfo, setAssetsInfo] = React.useState<AssetInfo[]>([]);

  const [assetsDataToExport, setAssetDataToExport] = React.useState<
    GridRowAssetTableInfo[]
  >([]);

  useEffect((): ReturnType<React.EffectCallback> => {
    if (assetData.AssetInfoList.length == 0) {
      getNodeAssetsInfo(dispatch, assetData.NodeID);
    } else {
      setAssetsInfo(assetData.AssetInfoList);
    }
    return (): void => {};
  }, []);

  const nodeAssetsInfo: AssetInfo[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.nodeAssetsInfo
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect((): ReturnType<React.EffectCallback> => {
    if (assetData.AssetInfoList.length == 0) setAssetsInfo(nodeAssetsInfo);
    return (): void => {};
  }, [nodeAssetsInfo]);

  const getCSVFileName = () => {
    return `assets_export_${assetData.SiteNumber}_${
      assetData.LaneNumber
    }_${dateFormat(currentLocale.language)}`;
  };

  const exportToCSV = () => {
    if (assetData != undefined) {
      let nodeInfo = assetData;
      nodeInfo.AssetInfoList = assetsInfo;

      let gridRowAssetInfo: GridRowAssetTableInfo[] =
        flattenAssetData(nodeInfo);

      setAssetDataToExport(gridRowAssetInfo);
    }
  };

  useEffect(() => {
    if (assetsDataToExport.length > 0 && csvLinkRef?.current)
      csvLinkRef.current.link.click();
  }, [assetsDataToExport]);

  return (
    <div className={classes.container}>
      <Box className={clsx(classes.spreadBox, classes.box)}>
        <Button
          themeColor={'primary'}
          type="submit"
          className={classes.dialogAction}
          onClick={closePage}
        >
          {GetString('BACK')}
        </Button>
        <Button
          themeColor={'primary'}
          type="submit"
          className={classes.dialogAction}
          onClick={exportToCSV}
        >
          {GetString('EXPORT_TO_CSV')}
        </Button>
      </Box>
      <CSVLink
        data={assetsDataToExport}
        ref={csvLinkRef}
        filename={getCSVFileName() + '.csv'}
      />

      <Grid
        container
        spacing={1}
        className={clsx(classes.gridAlign, classes.margin)}
      >
        <Grid item md={8} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('STORE')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {assetData.SiteNumber}
          </Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('DIVISION')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {assetData.Division}
          </Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('CLUSTER')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {assetData.NodeGroup}
          </Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('STATION_TYPE')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {assetData.NodeType}
          </Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('LANE')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {assetData.LaneNumber}
          </Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('NODE_ID')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {assetData.NodeID}
          </Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('LAST_HEARTBEAT_DATETIME')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {getLocalDateTime(new Date(assetData.LastHeartbeat))}
          </Typography>
        </Grid>
        <Grid item md={10} xs={10}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('ASSETS')}
          </Typography>
          <AssetDetailComponent assetInfo={assetsInfo} />
        </Grid>
      </Grid>
      <Divider />
    </div>
  );
};

export default NodeAssetsForm;
